/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

import getEnvironmentScopes from './authConfigEnvManager';
import { getDomainHintRequest } from './util/functions/domainHint';

const currentEnvironment = import.meta.env.VITE_ENVIRONMENT;

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: `${import.meta.env.VITE_B2C_SIGNUPSIGNIN_POLICY}`,
    forgotPassword: `${import.meta.env.VITE_B2C_FORGOT_PASSWD_POLICY}`,
    editProfile: `${import.meta.env.VITE_B2C_EDIT_PROFILE}`,
    changePassword: `${import.meta.env.VITE_B2C_PASSWORDCHANGE}`
  },
  authorities: {
    signUpSignIn: {
      authority: `${import.meta.env.VITE_AZURE_AD_B2C_DOMAIN_URL}/${import.meta.env.VITE_AZURE_AD_B2C_TENANT_POLICY_URL}/${import.meta.env.VITE_B2C_SIGNUPSIGNIN_POLICY}`
    },
    forgotPassword: {
      authority: `${import.meta.env.VITE_AZURE_AD_B2C_DOMAIN_URL}/${import.meta.env.VITE_AZURE_AD_B2C_TENANT_POLICY_URL}/B2C_1A_PASSWORDRESET`
    },
    editProfile: {
      authority: `${import.meta.env.VITE_AZURE_AD_B2C_DOMAIN_URL}/${import.meta.env.VITE_AZURE_AD_B2C_TENANT_POLICY_URL}/B2C_1A_PROFILEEDIT`
    },
    changePassword: {
      authority: `${import.meta.env.VITE_AZURE_AD_B2C_DOMAIN_URL}/${import.meta.env.VITE_AZURE_AD_B2C_TENANT_POLICY_URL}/B2C_1A_PASSWORDCHANGE`
    }
  },
  authorityDomain: `${import.meta.env.VITE_AZURE_AD_B2C_DOMAIN}`
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: `${import.meta.env.VITE_AZURE_AD_B2C_CLIENTID}`, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: `${import.meta.env.VITE_AZURE_AD_B2C_REDIRECT_URL}`, // "https://jwt.ms", // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: `${import.meta.env.VITE_CACHE_LOCATION}`, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Info:
            // console.info(message);
            break;
          case LogLevel.Verbose:
            // console.debug(message);
            break;
          case LogLevel.Warning:
            // console.warn(message);
            break;
          default:
            console.error(message);
            break;
        }
      }
    }
  }
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const ovverideToAllowAllSystemPermissionInDev =
  import.meta.env.VITE_ALLOW_ALL_USER_PERMISSIONS_IN_LOCAL_DEV === 'true';
export const protectedResources = getEnvironmentScopes();

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
/**
 * Deep-link tiles (e.g. `/?domain_hint=contoso.com`) forward a validated
 * `domain_hint` to the authorize request so Entra skips home-realm discovery.
 */
export const domainHintRequest = getDomainHintRequest(
  typeof window === 'undefined' ? '' : window.location.search
);

export const loginRequest = {
  scopes: [...protectedResources.apiRoles.scopes],
  ...domainHintRequest
};
